import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";
import { deleteKitIfNoCases } from "../utils/kitHelpers";
import toast from "react-hot-toast";

export default function KitsList() {
	const [kits, setKits] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const { role } = useAuth();
	const [openMenuId, setOpenMenuId] = useState(null);

	function toggleMenu(kitId) {
		console.log(kitId);
		setOpenMenuId((prev) => (prev === kitId ? null : kitId));
	}

	useEffect(() => {
		function handleClickOutside(e) {
			if (!e.target.closest(".list-item__actions")) setOpenMenuId(null);
		}
		document.addEventListener("click", handleClickOutside);
		return () => document.removeEventListener("click", handleClickOutside);
	}, []);

	useEffect(() => {
		fetchKits();
	}, []);

	const fetchKits = async () => {
		setLoading(true);

		const [
			{ data: kits, error: kitsError },
			{ data: gear, error: gearError },
		] = await Promise.all([
			supabase
				.from("kits")
				.select(
					`
				id,
				name,
				color,
				kit_cases (
					case_id
				)
			`
				)
				.order("name", { ascending: true }),

			supabase
				.from("gear")
				.select("case_id, gear_statuses(label)")
				.in("gear_statuses.label", [
					"Missing",
					"Needs Repair",
					"In Use",
				]),
		]);

		if (kitsError || gearError) {
			setError("Failed to fetch kits");
			console.error(kitsError || gearError);
			setLoading(false);
			return;
		}

		// Track which cases have missing/needs repair gear
		const caseStatusMap = {}; // case_id -> { missing: true/false, needsRepair: true/false }

		gear.forEach((g) => {
			const caseId = g.case_id;
			const label = g.gear_statuses?.label;

			if (!caseStatusMap[caseId]) {
				caseStatusMap[caseId] = {
					missing: false,
					needsRepair: false,
					inUse: false,
				};
			}
			if (label === "Missing") caseStatusMap[caseId].missing = true;
			if (label === "Needs Repair")
				caseStatusMap[caseId].needsRepair = true;
			if (label === "In Use") caseStatusMap[caseId].inUse = true;
		});

		// Determine per-kit status
		const kitsWithInfo = kits.map((kit) => {
			let hasMissing = false;
			let hasNeedsRepair = false;
			let hasInUse = false;

			for (const kc of kit.kit_cases) {
				const status = caseStatusMap[kc.case_id];
				if (status?.missing) hasMissing = true;
				if (status?.needsRepair) hasNeedsRepair = true;
				if (status?.inUse) hasInUse = true;
			}

			return {
				...kit,
				case_count: kit.kit_cases.length,
				hasMissing,
				hasNeedsRepair,
				hasInUse,
			};
		});

		setKits(kitsWithInfo);
		setLoading(false);
	};

	const handleDelete = async (kitId) => {
		setOpenMenuId(null);
		if (!window.confirm("Are you sure you want to delete this kit?"))
			return;

		const toastId = toast.loading("Deleting case...");
		const result = await deleteKitIfNoCases(kitId);

		if (!result.success) {
			toast.error(result.message, { id: toastId });
		} else {
			toast.success("Kit deleted successfully.", { id: toastId });
			await fetchKits();
		}
	};

	if (loading) {
		return (
			<div className="loading">
				<div>
					<i className="pi pi-spin pi-spinner"></i>
				</div>
			</div>
		);
	}
	if (error) return <p style={{ color: "red" }}>{error}</p>;
	//if (kits.length === 0) return <p>No kits found.</p>;

	return (
		<div className="main">
			<div className="action-lead">
				<h1>Kits</h1>
				{role === "admin" && (
					<Link to="/add-kit" className="button green">
						<i className="pi pi-plus"></i>
						Add Kit
					</Link>
				)}
			</div>

			<div className="kit-list__list-wrapper">
				{kits.length === 0 ? (
					<div>No kits found.</div>
				) : (
					kits.map((kit) => (
						<div key={kit.id} className="list-item has-color-bar">
							<div
								className="list-item__color"
								style={{
									backgroundColor: kit.color && kit.color,
								}}
							></div>
							<div className="list-item__name-row">
								<Link
									to={`/kits/${kit.id}`}
									className="list-item__name"
								>
									{kit.name}
								</Link>
								{kit.case_count > 0 && (
									<span className="list-item__count">
										({kit.case_count} case
										{kit.case_count > 1 && <>s</>})
									</span>
								)}
								{kit.hasNeedsRepair && (
									<span className="list-item__icon-wrapper repair">
										<i
											className="pi pi-wrench"
											title="Contains gear that needs repair"
										></i>
									</span>
								)}
								{kit.hasMissing && (
									<span className="list-item__icon-wrapper missing">
										<i
											className="pi pi-exclamation-triangle"
											title="Contains missing gear"
										></i>
									</span>
								)}
								{kit.hasInUse && (
									<span className="list-item__icon-wrapper inuse">
										<i
											className="pi pi-ban"
											title="Contains gear that is in use"
										></i>
									</span>
								)}
							</div>
							{role === "admin" && (
								<div className="list-item__actions">
									<button
										className="three-dot-button no-text"
										onClick={() => toggleMenu(kit.id)}
									>
										<i className="pi pi-ellipsis-v"></i>
									</button>
									{openMenuId === kit.id && (
										<ul className="list-item__actions-menu">
											<li
												onClick={() =>
													handleDelete(kit.id)
												}
												className="list-item__action-delete"
											>
												<i className="pi pi-trash"></i>
												Delete
											</li>
										</ul>
									)}
								</div>
							)}
						</div>
					))
				)}
			</div>
		</div>
	);
}
